import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { getAllExperiments, initialize, whenAvailable } from '@vp/ab-reader';
import { addAdditionalAttributes } from '@vp/om-newrelic-tracker';
import {
  buildActiveExperiments,
  buildAvailableExperiments,
} from './experimentsUtils';
import { supportedExperiments } from './experiments';

/**
 * This context provides a wrapper that manages the experiments for the application.
 * The expected structure of a single activeExperiment is:
 {
    experimentKey: string,  // the unique experimentKey identifier.
    variationKey: string,  // optional, the variationKey selected for the experiment.
    availableVariationKeys: [string], // the available variationKeys for the experiment.
    shouldFireImpression: bool // whether or not an impression should be fired when using this experiment.
                               // Notably, experiments that are not enabled for the site, not present for the current user, or have been modified via debug overrides will be false.
  }
 */
const ExperimentsContext = createContext(null);

const ExperimentsProvider = props => {
  const { children } = props;

  // Array of experiment objects for the given user as determined by @vp/ab-reader.
  const [assignedExperiments, setAssignedExperiments] = useState([]);

  // Array of experiments that are available for the application as determined by the supportedExperiments, and assignedExperiments.
  // See buildAvailableExperiments for more details.
  const availableExperiments = useMemo(
    () =>
      buildAvailableExperiments({
        supportedExperiments,
        assignedExperiments,
      }),
    [assignedExperiments]
  );

  // Array of experiments that can override an experiment in availableExperiments.
  const [debugExperimentOverrides, setDebugExperimentOverrides] = useState([]);

  // Array of experiments exposed to the rest of the application. Contains the result of applying the debug overrides to the available experiments.
  const activeExperiments = useMemo(
    () =>
      buildActiveExperiments({
        availableExperiments,
        debugExperimentOverrides,
      }),
    [availableExperiments, debugExperimentOverrides]
  );

  useEffect(() => {
    if (typeof window !== `undefined`) {
      // initialize the ab-reader, required after v3.x of ab-reader
      initialize();
      whenAvailable(async available => {
        // defaulted to 1000ms per ab-reader README example: https://gitlab.com/vistaprint-org/merchandising-technology/experimentation-platform/ab-libraries/ab-reader-js/-/tree/main
        if (available) {
          setAssignedExperiments(getAllExperiments());
        }
      }, 1000);
    }
  }, []);

  useEffect(() => {
    // filter out experiments that may be active but in which the user is not currently participating
    const currentExperiments = activeExperiments?.filter(
      exp => exp.variationKey !== undefined
    );
    if (currentExperiments?.length > 0) {
      const activeExperimentsString = currentExperiments
        ?.map(exp => `${exp.experimentKey}:${exp.variationKey}`)
        .join(',');
      // track experiments in new relic
      addAdditionalAttributes({
        activeExperiments: activeExperimentsString,
      });
    }
  }, [activeExperiments]);

  const overrideExperiment = useCallback(
    ({ experimentKey, variationKey, availableVariationKeys }) => {
      const debugExperimentOverridesClone = [...debugExperimentOverrides];
      const overrideExperimentIndex = debugExperimentOverridesClone.findIndex(
        experiment => experiment.experimentKey === experimentKey
      );

      // feature override already exists, set the variation key and fireImpression state
      if (overrideExperimentIndex > -1) {
        debugExperimentOverridesClone[overrideExperimentIndex] = {
          ...debugExperimentOverridesClone[overrideExperimentIndex],
          variationKey,
          shouldFireImpression: false,
        };
        setDebugExperimentOverrides(debugExperimentOverridesClone);
        return;
      }

      // feature override does not exist
      debugExperimentOverridesClone.push({
        experimentKey,
        variationKey,
        availableVariationKeys,
        shouldFireImpression: false,
      });
      setDebugExperimentOverrides(debugExperimentOverridesClone);
    },
    [debugExperimentOverrides]
  );

  const experimentsContextValues = useMemo(
    () => ({
      overrideExperiment,
      activeExperiments,
    }),
    [activeExperiments, overrideExperiment]
  );

  return (
    <ExperimentsContext.Provider value={experimentsContextValues}>
      {children}
    </ExperimentsContext.Provider>
  );
};

ExperimentsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export { ExperimentsContext, ExperimentsProvider };
